/**
 * Application state management for RSSio
 */

import { Capacitor } from "@capacitor/core";
import { API } from "./api.ts";
import { normalizeArticleId, isArticleReadInSet, isFutureArticle } from "./utils.ts";
import {
  getAllOfflineArticles,
  getAllOfflineArticleIds,
  saveArticleOffline,
  removeOfflineArticle,
  bulkDeleteOfflineArticles,
  bulkSaveArticlesOffline,
} from "./db.ts";

// Type definitions
export interface Subscription {
  id: string;
  url: string;
  title: string;
  category?: string;
  icon?: string;
  fullArticleMode?: boolean;
  fullArticle?: boolean;
  addedAt?: string;
  order?: number;
  lastError?: string;
}

export interface Category {
  id: string;
  name: string;
  order?: number;
  createdAt?: string;
}

export interface Article {
  id?: string;
  guid?: string;
  link?: string | string[] | { href?: string; $?: { href?: string } };
  title?: string;
  content?: string;
  description?: string;
  summary?: string;
  pubDate?: string;
  author?: string;
  feedId?: string;
  feedTitle?: string;
  feedIcon?: string;
  videoId?: string;
  imageUrl?: string;
  enclosure?: { url?: string; type?: string; $?: { url?: string; type?: string } };
  "media:thumbnail"?: { $?: { url?: string } };
  "media:content"?: { $?: { url?: string } };
}

export interface SavedArticleFallback {
  link: string;
  title?: string;
  feedTitle?: string;
  pubDate?: string;
  description?: string;
  imageUrl?: string;
  sourceType?: "discover" | "external" | "feed";
  fullContent?: string;
  fullContentSource?: string | null;
  savedAt: number;
}

export type EventType = "concert" | "theater" | "festival" | "sports" | "exhibition" | "cinema" | "other";

export type EventCategory = "music" | "festival" | "theater" | "cinema" | "other";

export interface EventCategoryMeta {
  id: EventCategory;
  label: string;
  icon: string;
  color: string;
}

export const CATEGORY_META: Record<EventCategory, EventCategoryMeta> = {
  music: { id: "music", label: "Music", icon: "music_note", color: "#ec4899" },
  festival: { id: "festival", label: "Festival", icon: "celebration", color: "#f59e0b" },
  theater: { id: "theater", label: "Theater", icon: "theater_comedy", color: "#8b5cf6" },
  cinema: { id: "cinema", label: "Cinema", icon: "movie", color: "#3b82f6" },
  other: { id: "other", label: "Other", icon: "event", color: "#6b7280" },
};

/**
 * Map an AI-returned eventType to a user-facing category.
 * music ← concert, sports
 * festival ← festival
 * theater ← theater, exhibition
 * cinema ← cinema
 * other ← other
 */
export function getEventCategory(eventType: EventType | string | undefined | null): EventCategory {
  switch (eventType) {
    case "concert":
    case "sports":
      return "music";
    case "festival":
      return "festival";
    case "theater":
    case "exhibition":
      return "theater";
    case "cinema":
      return "cinema";
    default:
      return "other";
  }
}

export interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  startDate: string;
  endDate?: string;
  location?: string;
  sourceUrl: string;
  sourceFeedId: string;
  sourceFeedTitle?: string;
  sourceArticleId?: string;
  eventType?: EventType;
  createdAt: string;
  detectedAt: string;
}

export interface CalendarFeed {
  id: string;
  url: string;
  title: string;
  category?: string;
  lastScannedAt?: string;
  eventCount: number;
}

export interface PerspectiveItem {
  url: string;
  source: string;
  lean?: string;
  tag: string;
  tagLabel?: string;
  title: string;
  angle?: string;
}

export interface FullArticleData {
  content: string;
  source?: string | null;
  isFullContent?: boolean;
  image?: string | null;
  title?: string;
  excerpt?: string;
  textContent?: string;
}

export type FullArticleContentEntry = FullArticleData | string;

export interface User {
  id: string;
  email: string;
  displayName?: string;
  name?: string;
  photo?: string;
  picture?: string;
}

export interface AppState {
  user: User | null;
  subscriptions: Subscription[];
  categories: Category[];
  readItems: Set<string>;
  favorites: Set<string>;
  readLater: Set<string>;
  savedFallbacks: Record<string, SavedArticleFallback>;
  expandedFeeds: Set<string>;
  feedCounts: Record<string, number>;
  expandedCategories: Set<string>;
  selectedCategory: string | null;
  selectedFeed: string | null;
  openMenuId: string | null;
  openCategoryMenu: string | null;
  headerMenuOpen: boolean;
  mobileMenuOpen: boolean;
  expandedArticleId: string | null;
  showUnreadOnly: boolean;
  fullArticleMode: boolean;
  feedFullArticle: Record<string, boolean>;
  fullArticleContent: Record<string, FullArticleContentEntry>;
  offlineArticleAssets: Record<string, { imageUrl?: string; videoUrl?: string; videoFilePath?: string; hasVideo?: boolean }>;
  isLoadingArticle: boolean;
  isOnline: boolean;
  isLoadingFeeds: boolean;
  initialFeedLoadComplete: boolean;
  isMergingBackgroundArticles: boolean;
  userMenuOpen: boolean;
  lastRefreshTime: number;
  displayedArticleCount: number;
  articleSummaries: Record<string, string>;
  summaryCollapsed: Record<string, boolean>;
  isLoadingSummary: boolean;
  loadingSummaryId: string | null;
  isLoadingSummaryTts: boolean;
  loadingSummaryTtsId: string | null;
  playingSummaryTtsId: string | null;
  articlePerspectives: Record<string, { perspectives?: PerspectiveItem[] } | PerspectiveItem[]>;
  perspectivesCollapsed: Record<string, boolean>;
  isLoadingPerspectives: boolean;
  loadingPerspectivesId: string | null;
  refreshInProgress: boolean;
  loadedFeedsCount: number;
  syncStatus: "synced" | "syncing" | "error" | "offline";
  lastSyncAt: number;
  // Calendar
  calendarFeeds: CalendarFeed[];
  events: CalendarEvent[];
  selectedDate: string | null;
  calendarViewMode: "month" | "list";
  isScanningCalendar: boolean;
  lastCalendarScanAt: number;
  // null = all categories, otherwise an EventCategory
  calendarCategoryFilter: EventCategory | null;
}

export type FeedItems = Record<string, Article[]>;

// Memoization cache for expensive calculations
export const memoCache = {
  unreadCounts: new Map<string, number>(),
  lastReadItemsCount: 0,
  lastFeedItemsHash: "",
};

export interface ArticleIndexEntry {
  article: Article;
  feedId: string;
  feedTitle: string;
  subscription?: Subscription;
}

let articleIndex: Map<string, ArticleIndexEntry> | null = null;

export function invalidateArticleIndex(): void {
  articleIndex = null;
}

export function getArticleIndex(): Map<string, ArticleIndexEntry> {
  if (articleIndex !== null) {
    return articleIndex;
  }
  const index = new Map<string, ArticleIndexEntry>();
  const subMap = new Map<string, Subscription>();
  for (const sub of state.subscriptions || []) {
    subMap.set(sub.id, sub);
  }

  for (const [feedId, items] of Object.entries(feedItems || {})) {
    const sub = subMap.get(feedId);
    const feedTitle = sub?.title || "Feed";
    for (const item of items || []) {
      const id = normalizeArticleId(item);
      const entry: ArticleIndexEntry = { article: item, feedId, feedTitle, subscription: sub };
      if (id) {
        index.set(id, entry);
      }
      if (item.guid && typeof item.guid === "string") {
        index.set(item.guid, entry);
      }
      if (item.link && typeof item.link === "string") {
        index.set(item.link, entry);
      }
    }
  }
  articleIndex = index;
  return index;
}

/**
 * Fast O(1) lookup to find an article and its originating feed by ID.
 */
export function findArticleById(articleId: string): ArticleIndexEntry | null {
  if (!articleId) return null;
  const index = getArticleIndex();
  return index.get(articleId) || null;
}

// Invalidate cache when state changes
export function invalidateMemoCache(): void {
  memoCache.unreadCounts.clear();
  invalidateArticleIndex();
}

export function invalidateMemoCacheForFeeds(feedIds: string[]): void {
  if (!feedIds || feedIds.length === 0) return;
  const targets = new Set(feedIds.map((id) => String(id)));

  Array.from(memoCache.unreadCounts.keys()).forEach((key) => {
    const ids = key.split(",");
    if (ids.some((id) => targets.has(id))) {
      memoCache.unreadCounts.delete(key);
    }
  });
}

export function invalidateMemoCacheForArticle(articleId: string): void {
  const touchedFeedIds: string[] = [];
  Object.entries(feedItems || {}).forEach(([feedId, items]) => {
    if (items.some((item) => normalizeArticleId(item) === articleId)) {
      touchedFeedIds.push(feedId);
    }
  });

  if (touchedFeedIds.length === 0) {
    invalidateMemoCache();
    return;
  }

  invalidateMemoCacheForFeeds(touchedFeedIds);
}

// Get cached unread count for a subscription list
export function getCachedUnreadCount(
  subscriptions: Subscription[],
  feedItemsParam: FeedItems,
  readItems: Set<string>
): number {
  const subIds = subscriptions
    .map((s) => s.id)
    .sort()
    .join(",");

  if (memoCache.lastReadItemsCount !== readItems.size) {
    memoCache.unreadCounts.clear();
    memoCache.lastReadItemsCount = readItems.size;
  }

  if (memoCache.unreadCounts.has(subIds)) {
    return memoCache.unreadCounts.get(subIds)!;
  }

  let count = 0;
  subscriptions.forEach((sub) => {
    const items = feedItemsParam?.[sub.id] || [];
    items.forEach((item) => {
      if (!isFutureArticle(item) && !isArticleReadInSet(item, readItems)) {
        count++;
      }
    });
  });

  memoCache.unreadCounts.set(subIds, count);
  return count;
}

// Feed items storage
export const feedItems: FeedItems = {};

// Application state
export const state: AppState = {
  user: null,
  subscriptions: [],
  categories: [],
  readItems: new Set(),
  favorites: new Set(),
  readLater: new Set(),
  savedFallbacks: {},
  expandedFeeds: new Set(),
  feedCounts: {},
  expandedCategories: new Set(),
  selectedCategory: null,
  selectedFeed: null,
  openMenuId: null,
  openCategoryMenu: null,
  headerMenuOpen: false,
  mobileMenuOpen: false,
  expandedArticleId: null,
  showUnreadOnly: true,
  fullArticleMode: false,
  feedFullArticle: {},
  fullArticleContent: {},
  offlineArticleAssets: {},
  isLoadingArticle: false,
  isOnline: navigator.onLine,
  isLoadingFeeds: false,
  initialFeedLoadComplete: false,
  isMergingBackgroundArticles: false,
  userMenuOpen: false,
  lastRefreshTime: 0,
  displayedArticleCount: 50,
  articleSummaries: {},
  summaryCollapsed: {},
  isLoadingSummary: false,
  loadingSummaryId: null,
  isLoadingSummaryTts: false,
  loadingSummaryTtsId: null,
  playingSummaryTtsId: null,
  articlePerspectives: {},
  perspectivesCollapsed: {},
  isLoadingPerspectives: false,
  loadingPerspectivesId: null,
  refreshInProgress: false,
  loadedFeedsCount: 0,
  syncStatus: "synced",
  lastSyncAt: 0,
  // Calendar
  calendarFeeds: [],
  events: [],
  selectedDate: null,
  calendarViewMode: "month",
  isScanningCalendar: false,
  lastCalendarScanAt: 0,
  calendarCategoryFilter: null,
};

// Reset state to initial values
export function resetState() {
  state.user = null;
  state.subscriptions = [];
  state.categories = [];
  state.readItems = new Set();
  state.favorites = new Set();
  state.readLater = new Set();
  state.savedFallbacks = {};
  state.expandedFeeds = new Set();
  state.feedCounts = {};
  state.expandedCategories = new Set();
  state.selectedCategory = null;
  state.selectedFeed = null;
  state.openMenuId = null;
  state.openCategoryMenu = null;
  state.headerMenuOpen = false;
  state.mobileMenuOpen = false;
  state.articleSummaries = {};
  state.summaryCollapsed = {};
  state.articlePerspectives = {};
  state.perspectivesCollapsed = {};
  state.isLoadingSummaryTts = false;
  state.loadingSummaryTtsId = null;
  state.playingSummaryTtsId = null;
  state.expandedArticleId = null;
  state.showUnreadOnly = true;
  state.fullArticleMode = false;
  state.feedFullArticle = {};
  state.fullArticleContent = {};
  state.offlineArticleAssets = {};
  state.isLoadingArticle = false;
  state.isLoadingFeeds = false;
  state.initialFeedLoadComplete = false;
  state.isMergingBackgroundArticles = false;
  state.userMenuOpen = false;
  state.lastRefreshTime = 0;
  state.displayedArticleCount = 50;
  state.syncStatus = "synced";
  state.lastSyncAt = 0;
  // Calendar
  state.calendarFeeds = [];
  state.events = [];
  state.selectedDate = null;
  state.calendarViewMode = "month";
  state.isScanningCalendar = false;
  state.lastCalendarScanAt = 0;
  state.calendarCategoryFilter = null;
  invalidateMemoCache();
}

const SAVED_FALLBACKS_CACHE_KEY = "rssio-saved-fallbacks-v1";
// Each fallback's metadata is small, but a typical localStorage budget is
// ~5MB. Keep a hard ceiling so a single oversized article doesn't break the
// cache, and keep at most the N most-recently-saved entries.
const SAVED_FALLBACKS_CACHE_MAX_ENTRIES = 200;
const SAVED_FALLBACKS_CACHE_MAX_BYTES = 2 * 1024 * 1024; // 2 MB

/**
 * Build a compact version of a fallback suitable for localStorage. The full
 * article body lives in IndexedDB (see db.ts) and doesn't need to be mirrored
 * here; storing only metadata keeps the cache small and prevents QuotaErrors.
 */
function compactSavedFallback(fallback: SavedArticleFallback): SavedArticleFallback {
  return {
    link: fallback.link,
    title: fallback.title,
    feedTitle: fallback.feedTitle,
    pubDate: fallback.pubDate,
    description:
      typeof fallback.description === "string"
        ? fallback.description.substring(0, 1000)
        : fallback.description,
    imageUrl: fallback.imageUrl,
    sourceType: fallback.sourceType,
    fullContentSource: fallback.fullContentSource,
    savedAt: fallback.savedAt,
    // fullContent intentionally omitted — IndexedDB is the source of truth.
  };
}

/**
 * Compact an entire savedFallbacks map for localStorage persistence.
 * Drops fullContent everywhere and keeps only the N most recent entries.
 */
function compactSavedFallbacksMap(
  map: Record<string, SavedArticleFallback>,
): Record<string, SavedArticleFallback> {
  if (!map || typeof map !== "object") return {};
  const entries = Object.entries(map)
    .map(([id, fb]) => [id, compactSavedFallback(fb)] as [string, SavedArticleFallback])
    // Keep the most-recently-saved first.
    .sort((a, b) => (b[1].savedAt || 0) - (a[1].savedAt || 0))
    .slice(0, SAVED_FALLBACKS_CACHE_MAX_ENTRIES);
  const out: Record<string, SavedArticleFallback> = {};
  for (const [id, fb] of entries) out[id] = fb;
  return out;
}

function syncSavedFallbacks(
  { add = {}, remove = [] }: { add?: Record<string, SavedArticleFallback>; remove?: string[] },
): Promise<void> {
  const addKeys = Object.keys(add || {});
  const removeIds = (remove || []).map((id) => String(id));
  if (addKeys.length === 0 && removeIds.length === 0) return Promise.resolve();

  return API.post("/drive/sync-state", {
    savedFallbacks: {
      add,
      remove: removeIds,
    },
  })
    .then(() => undefined)
    .catch((error) => {
      console.warn("[SavedFallbacks] Sync failed:", (error as Error)?.message || error);
    });
}

export function saveSavedFallbacksCache(): void {
  const compact = compactSavedFallbacksMap(state.savedFallbacks || {});
  const serialized = JSON.stringify(compact);

  // Belt-and-braces: if even the compact form exceeds the byte cap, drop the
  // oldest entries until it fits, so the cache never grows unbounded.
  let payload = serialized;
  if (payload.length > SAVED_FALLBACKS_CACHE_MAX_BYTES) {
    const ids = Object.keys(compact).sort(
      (a, b) => (compact[b].savedAt || 0) - (compact[a].savedAt || 0),
    );
    const trimmed: Record<string, SavedArticleFallback> = {};
    for (const id of ids) {
      trimmed[id] = compact[id];
      payload = JSON.stringify(trimmed);
      if (payload.length <= SAVED_FALLBACKS_CACHE_MAX_BYTES) break;
    }
    try {
      localStorage.setItem(SAVED_FALLBACKS_CACHE_KEY, payload);
      console.warn(
        `[Cache] Trimmed saved fallbacks cache to fit ${SAVED_FALLBACKS_CACHE_MAX_BYTES} byte budget.`,
      );
      return;
    } catch (e) {
      console.warn("[Cache] Failed to save saved fallbacks after trim:", e);
      return;
    }
  }

  try {
    localStorage.setItem(SAVED_FALLBACKS_CACHE_KEY, payload);
  } catch (e) {
    console.warn("[Cache] Failed to save saved fallbacks:", e);
    // QuotaExceededError or similar — degrade gracefully: try an even smaller
    // subset of the most-recent entries so the user at least keeps the latest.
    try {
      const ids = Object.keys(compact).sort(
        (a, b) => (compact[b].savedAt || 0) - (compact[a].savedAt || 0),
      );
      const half = Math.max(1, Math.floor(ids.length / 2));
      const trimmed: Record<string, SavedArticleFallback> = {};
      for (const id of ids.slice(0, half)) trimmed[id] = compact[id];
      localStorage.setItem(SAVED_FALLBACKS_CACHE_KEY, JSON.stringify(trimmed));
      console.warn(
        `[Cache] Reduced saved fallbacks cache to ${half} entries due to quota.`,
      );
    } catch (e2) {
      console.warn("[Cache] Failed to save saved fallbacks:", e2);
    }
  }
}

export function loadSavedFallbacksCache(): void {
  try {
    const raw = localStorage.getItem(SAVED_FALLBACKS_CACHE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      state.savedFallbacks = parsed as Record<string, SavedArticleFallback>;
    }
  } catch (e) {
    console.warn("[Cache] Failed to load saved fallbacks:", e);
  }
}

export const USER_CACHE_KEY = "rssio-user-v1";
export const SUBSCRIPTIONS_CACHE_KEY = "rssio-subscriptions-v1";
export const CATEGORIES_CACHE_KEY = "rssio-categories-v1";
export const SYNC_STATE_CACHE_KEY = "rssio-sync-state-v1";

export function saveUserCache(): void {
  try {
    if (state.user && state.user.displayName) {
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(state.user));
    }
  } catch (e) {
    console.warn("[Cache] Failed to save user:", e);
  }
}

export function loadUserCache(): boolean {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    if (!raw) return false;
    const user = JSON.parse(raw);
    if (user && user.displayName) {
      state.user = user;
      return true;
    }
  } catch (e) {
    console.warn("[Cache] Failed to load user:", e);
  }
  return false;
}

export function clearUserCache(): void {
  try {
    localStorage.removeItem(USER_CACHE_KEY);
  } catch (e) {
    console.warn("[Cache] Failed to clear user cache:", e);
  }
}

export function clearAllLocalCaches(): void {
  try {
    localStorage.removeItem(USER_CACHE_KEY);
    localStorage.removeItem(SUBSCRIPTIONS_CACHE_KEY);
    localStorage.removeItem(CATEGORIES_CACHE_KEY);
    localStorage.removeItem(SYNC_STATE_CACHE_KEY);
    localStorage.removeItem(SAVED_FALLBACKS_CACHE_KEY);
    localStorage.removeItem("rssio-feed-cache-v2");
    localStorage.removeItem("rssio-feed-cache-v2-time");
  } catch (e) {
    console.warn("[Cache] Failed to clear local caches:", e);
  }
}

export function saveSubscriptionsCache(): void {
  try {
    if (Array.isArray(state.subscriptions) && state.subscriptions.length > 0) {
      localStorage.setItem(SUBSCRIPTIONS_CACHE_KEY, JSON.stringify(state.subscriptions));
    }
  } catch (e) {
    console.warn("[Cache] Failed to save subscriptions:", e);
  }
}

export function loadSubscriptionsCache(): boolean {
  try {
    const raw = localStorage.getItem(SUBSCRIPTIONS_CACHE_KEY);
    if (!raw) return false;
    const subs = JSON.parse(raw);
    if (Array.isArray(subs) && subs.length > 0) {
      state.subscriptions = subs;
      return true;
    }
  } catch (e) {
    console.warn("[Cache] Failed to load subscriptions:", e);
  }
  return false;
}

export function saveCategoriesCache(): void {
  try {
    if (Array.isArray(state.categories) && state.categories.length > 0) {
      localStorage.setItem(CATEGORIES_CACHE_KEY, JSON.stringify(state.categories));
    }
  } catch (e) {
    console.warn("[Cache] Failed to save categories:", e);
  }
}

export function loadCategoriesCache(): boolean {
  try {
    const raw = localStorage.getItem(CATEGORIES_CACHE_KEY);
    if (!raw) return false;
    const cats = JSON.parse(raw);
    if (Array.isArray(cats)) {
      state.categories = cats;
      return true;
    }
  } catch (e) {
    console.warn("[Cache] Failed to load categories:", e);
  }
  return false;
}

export function saveSyncStateToCache(): void {
  try {
    const cacheData = {
      readItems: Array.from(state.readItems || []),
      favorites: Array.from(state.favorites || []),
      readLater: Array.from(state.readLater || []),
      baseline: {
        readItems: Array.from(syncMetadata.readItems?.baseline || []),
        favorites: Array.from(syncMetadata.favorites?.baseline || []),
        readLater: Array.from(syncMetadata.readLater?.baseline || []),
      },
      updatedAt: {
        readItems: syncMetadata.readItems?.updatedAt || 0,
        favorites: syncMetadata.favorites?.updatedAt || 0,
        readLater: syncMetadata.readLater?.updatedAt || 0,
      },
      lastAppliedServerTime,
      lastAppliedRevision,
    };
    localStorage.setItem(SYNC_STATE_CACHE_KEY, JSON.stringify(cacheData));
  } catch (e) {
    console.warn("[Cache] Failed to save sync state to cache:", e);
  }
}

export function loadSyncStateFromCache(): boolean {
  try {
    const raw = localStorage.getItem(SYNC_STATE_CACHE_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw);
    if (!data || typeof data !== "object") return false;

    if (Array.isArray(data.readItems)) {
      state.readItems = purgeBogusArticleIds(new Set(data.readItems.map(String)));
    }
    if (Array.isArray(data.favorites)) {
      state.favorites = new Set(data.favorites.map(String));
    }
    if (Array.isArray(data.readLater)) {
      state.readLater = new Set(data.readLater.map(String));
    }
    if (data.baseline && typeof data.baseline === "object") {
      if (Array.isArray(data.baseline.readItems)) {
        syncMetadata.readItems.baseline = new Set(data.baseline.readItems.map(String));
      }
      if (Array.isArray(data.baseline.favorites)) {
        syncMetadata.favorites.baseline = new Set(data.baseline.favorites.map(String));
      }
      if (Array.isArray(data.baseline.readLater)) {
        syncMetadata.readLater.baseline = new Set(data.baseline.readLater.map(String));
      }
    }
    if (data.updatedAt && typeof data.updatedAt === "object") {
      syncMetadata.readItems.updatedAt = Number(data.updatedAt.readItems || 0);
      syncMetadata.favorites.updatedAt = Number(data.updatedAt.favorites || 0);
      syncMetadata.readLater.updatedAt = Number(data.updatedAt.readLater || 0);
    }
    if (data.lastAppliedServerTime) {
      lastAppliedServerTime = Number(data.lastAppliedServerTime || 0);
    }
    if (data.lastAppliedRevision) {
      lastAppliedRevision = Number(data.lastAppliedRevision || 0);
    }
    invalidateMemoCache();
    return true;
  } catch (e) {
    console.warn("[Cache] Failed to load sync state from cache:", e);
    return false;
  }
}

export function upsertSavedFallback(articleId: string, fallback: SavedArticleFallback): Promise<void> {
  state.savedFallbacks[articleId] = fallback;
  try {
    saveSavedFallbacksCache();
  } catch (e) {
    console.warn("[Cache] Failed to persist saved fallbacks (quota exceeded):", e);
  }
  return syncSavedFallbacks({ add: { [articleId]: fallback } });
}

export function upsertExternalSavedFallback(
  articleId: string,
  url: string,
  title: string,
  feedTitle = "Saved from Web",
  options?: {
    pubDate?: string;
    description?: string;
    imageUrl?: string;
    sourceType?: "discover" | "external" | "feed";
    fullContent?: string;
    fullContentSource?: string | null;
  },
): Promise<void> {
  if (!url) return Promise.resolve();
  return upsertSavedFallback(articleId, {
    link: url,
    title: title || url,
    feedTitle,
    pubDate: options?.pubDate,
    description: options?.description,
    imageUrl: options?.imageUrl,
    sourceType: options?.sourceType,
    fullContent: options?.fullContent,
    fullContentSource: options?.fullContentSource,
    savedAt: Date.now(),
  });
}

export function removeSavedFallback(articleId: string): Promise<void> {
  if (!state.savedFallbacks[articleId]) return Promise.resolve();
  delete state.savedFallbacks[articleId];
  saveSavedFallbacksCache();
  return syncSavedFallbacks({ remove: [articleId] });
}

export function pruneSavedFallbackForArticle(articleId: string): void {
  const keep = (state.favorites?.has(articleId) || state.readLater?.has(articleId));
  if (!keep) {
    void removeSavedFallback(articleId);
  }
}

export function pruneSavedFallbacks(): void {
  const keep = new Set<string>([
    ...Array.from(state.favorites || []),
    ...Array.from(state.readLater || []),
    // Also preserve fallbacks for articles that have offline content cached
    ...Object.keys(state.offlineArticleAssets || {}),
  ]);

  let changed = false;
  const removedIds: string[] = [];
  Object.keys(state.savedFallbacks || {}).forEach((articleId) => {
    if (!keep.has(articleId)) {
      delete state.savedFallbacks[articleId];
      removedIds.push(articleId);
      changed = true;
    }
  });

  if (changed) {
    saveSavedFallbacksCache();
    void syncSavedFallbacks({ remove: removedIds });
  }
}

function getOfflineArticleLink(article: Article | Record<string, any>): string {
  const link = article?.link;
  if (!link) return "";

  if (typeof link === "string") {
    return link;
  }

  if (Array.isArray(link) && link.length > 0) {
    const firstLink = link[0];
    if (typeof firstLink === "string") {
      return firstLink;
    }
    return firstLink?.href || "";
  }

  if (typeof link === "object") {
    return link.href || link.$?.href || "";
  }

  return "";
}

function getSavedFallbackVideoId(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = String(value).match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  );
  return match ? match[1] : null;
}

function addOfflineArticleLookupKey(
  lookup: Map<string, Record<string, any>>,
  key: string | null | undefined,
  article: Record<string, any>,
): void {
  const normalizedKey = String(key || "").trim();
  if (normalizedKey && !lookup.has(normalizedKey)) {
    lookup.set(normalizedKey, article);
  }
}

function addOfflineArticleLookupKeys(
  lookup: Map<string, Record<string, any>>,
  article: Record<string, any>,
): void {
  addOfflineArticleLookupKey(lookup, article.id, article);
  addOfflineArticleLookupKey(lookup, normalizeArticleId(article), article);

  const link = getOfflineArticleLink(article);
  addOfflineArticleLookupKey(lookup, link, article);

  const videoId =
    (typeof article.videoId === "string" && article.videoId) ||
    getSavedFallbackVideoId(String(article.id || "")) ||
    getSavedFallbackVideoId(link);
  if (videoId) {
    addOfflineArticleLookupKey(lookup, `yt:${videoId}`, article);
  }
}

function getSavedArticleLookupKeys(articleId: string, fallback?: SavedArticleFallback): string[] {
  const keys = new Set<string>([articleId]);
  const fallbackLink = fallback?.link || "";
  if (fallbackLink.trim()) {
    keys.add(fallbackLink);
  }

  const videoId =
    getSavedFallbackVideoId(articleId) ||
    getSavedFallbackVideoId(fallbackLink);
  if (videoId) {
    keys.add(`yt:${videoId}`);
  }

  return Array.from(keys);
}

/**
 * Migration: Restore savedFallbacks for saved articles that have offline content
 * but missing savedFallback entries. This fixes articles saved before the
 * cacheArticleOffline savedFallback fix was added.
 */
export async function migrateMissingSavedFallbacks(): Promise<void> {
  const savedArticleIds = new Set([
    ...Array.from(state.favorites || []),
    ...Array.from(state.readLater || []),
  ]);

  if (savedArticleIds.size === 0) return;

  let offlineArticlesById = new Map<string, Record<string, any>>();
  try {
    const offlineArticles = await getAllOfflineArticles();
    offlineArticlesById = new Map();
    offlineArticles
      .filter((article) => article?.id)
      .forEach((article) => addOfflineArticleLookupKeys(offlineArticlesById, article));
  } catch (err) {
    console.warn("[Migration] Failed to load offline articles for saved fallbacks:", err);
  }

  let migrated = 0;
  savedArticleIds.forEach((articleId) => {
    const existingFallback = state.savedFallbacks?.[articleId];
    const existingFallbackHasLink = Boolean(existingFallback?.link?.trim());
    if (existingFallbackHasLink) return;

    const offlineArticle = getSavedArticleLookupKeys(articleId, existingFallback)
      .map((key) => offlineArticlesById.get(key))
      .find(Boolean);
    if (offlineArticle) {
      const link = getOfflineArticleLink(offlineArticle);
      state.savedFallbacks[articleId] = {
        ...existingFallback,
        link,
        title: offlineArticle.title || existingFallback?.title || link || "Offline Article",
        feedTitle: offlineArticle.feedTitle || existingFallback?.feedTitle || "Saved",
        pubDate: offlineArticle.pubDate || existingFallback?.pubDate,
        description: offlineArticle.description || existingFallback?.description || offlineArticle.content || "",
        imageUrl: offlineArticle.imageUrl || existingFallback?.imageUrl,
        sourceType: "feed",
        fullContent: offlineArticle.content || existingFallback?.fullContent || "",
        fullContentSource: offlineArticle.source || existingFallback?.fullContentSource || null,
        savedAt: offlineArticle.savedAt || existingFallback?.savedAt || Date.now(),
      };
      migrated++;
      return;
    }

    // Check if there's offline asset content for this article
    const offlineAsset = state.offlineArticleAssets?.[articleId];
    if (!offlineAsset) return;

    // Create a savedFallback from offline asset
    state.savedFallbacks[articleId] = {
      ...existingFallback,
      link: offlineAsset.videoFilePath || offlineAsset.videoUrl || "",
      title: existingFallback?.title || "Offline Article",
      feedTitle: existingFallback?.feedTitle || "Saved",
      savedAt: existingFallback?.savedAt || Date.now(),
      description: existingFallback?.description || "",
      imageUrl: offlineAsset.imageUrl || existingFallback?.imageUrl,
      sourceType: "feed",
    };
    migrated++;
  });

  if (migrated > 0) {
    console.log(`[Migration] Created ${migrated} missing savedFallbacks from offline cache`);
    saveSavedFallbacksCache();
    // Don't sync immediately - let the normal sync handle it
  }
}

type SyncKey = "readItems" | "favorites" | "readLater";

let lastAppliedServerTime = 0;
let lastAppliedRevision = 0;

export function getLastAppliedRevision(): number {
  return lastAppliedRevision;
}

export function resetSyncRevisionsForTesting(): void {
  lastAppliedRevision = 0;
  lastAppliedServerTime = 0;
  syncMetadata.readItems = { baseline: new Set(), updatedAt: 0 };
  syncMetadata.favorites = { baseline: new Set(), updatedAt: 0 };
  syncMetadata.readLater = { baseline: new Set(), updatedAt: 0 };
}
const syncMetadata: Record<SyncKey, { baseline: Set<string>; updatedAt: number }> = {
  readItems: { baseline: new Set<string>(), updatedAt: 0 },
  favorites: { baseline: new Set<string>(), updatedAt: 0 },
  readLater: { baseline: new Set<string>(), updatedAt: 0 },
};

function setSyncStatus(status: AppState["syncStatus"]): void {
  state.syncStatus = status;
  if (status === "synced") {
    state.lastSyncAt = Date.now();
  }
}

export function diffSets(current: Set<string>, baseline: Set<string>): { add: string[]; remove: string[] } {
  const add: string[] = [];
  const remove: string[] = [];

  current.forEach((id) => {
    if (!baseline.has(id)) add.push(id);
  });

  baseline.forEach((id) => {
    if (!current.has(id)) remove.push(id);
  });

  return { add, remove };
}

function setFromKey(key: SyncKey): Set<string> {
  if (key === "readItems") return state.readItems;
  if (key === "favorites") return state.favorites;
  return state.readLater;
}

function setStateFromKey(key: SyncKey, value: Set<string>): void {
  if (key === "readItems") {
    state.readItems = purgeBogusArticleIds(value);
  } else if (key === "favorites") {
    state.favorites = value;
  } else {
    state.readLater = value;
  }
}

// ponytail: a previous mark-all-as-read bug added "[object Object]" and
// similar stringified-object entries into readItems whenever an item had
// a non-string guid. Strip them out so the unread counter is accurate.
function purgeBogusArticleIds(set: Set<string>): Set<string> {
  let purged = false;
  for (const id of set) {
    if (!id || id === "[object Object]" || /^\[object /.test(id)) {
      set.delete(id);
      purged = true;
    }
  }
  return purged ? new Set(set) : set;
}

function updateBaseline(key: SyncKey, items: string[], updatedAt = Date.now()): void {
  const normalized = new Set((items || []).map((id) => String(id)));
  syncMetadata[key].baseline = normalized;
  syncMetadata[key].updatedAt = Number(updatedAt || Date.now());
}

export function areSetsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  return Array.from(a).every((id) => b.has(id));
}

export function normalizeIncomingCollection(incoming: unknown): { items: string[]; updatedAt: number } {
  const incomingRecord =
    incoming && typeof incoming === "object" && !Array.isArray(incoming)
      ? incoming as Record<string, unknown>
      : null;
  const rawIncomingItems: unknown[] = Array.isArray(incomingRecord?.items)
    ? incomingRecord.items
    : Array.isArray(incoming)
      ? (incoming as unknown[])
      : [];
  return {
    items: rawIncomingItems.map((id) => String(id)),
    updatedAt: Number(incomingRecord?.updatedAt || Date.now()),
  };
}

export function resolveCollectionState(
  current: Set<string>,
  baseline: Set<string>,
  incomingSet: Set<string>,
): Set<string> | null {
  const localDiff = diffSets(current, baseline);
  const hasPendingLocalChanges =
    localDiff.add.length > 0 || localDiff.remove.length > 0;

  const serverMatchesBaseline =
    incomingSet.size === baseline.size &&
    Array.from(incomingSet).every((id) => baseline.has(id));

  // Server is echoing known baseline from an unrelated save; preserve pending local changes.
  if (serverMatchesBaseline && hasPendingLocalChanges) {
    return null;
  }

  if (areSetsEqual(current, incomingSet)) {
    return null;
  }

  if (hasPendingLocalChanges) {
    // Server changed on another device while local changes are pending: merge both.
    const merged = new Set<string>(incomingSet);
    localDiff.add.forEach((id) => merged.add(id));
    localDiff.remove.forEach((id) => merged.delete(id));
    return merged;
  }

  return incomingSet;
}

function mergeSavedFallbacksFromSync(syncState: unknown): boolean {
  if (!syncState || typeof syncState !== "object" || Array.isArray(syncState)) {
    return false;
  }

  const savedFallbacks = (syncState as Record<string, unknown>).savedFallbacks;
  if (!savedFallbacks || typeof savedFallbacks !== "object" || Array.isArray(savedFallbacks)) {
    return false;
  }

  const incoming = savedFallbacks as Record<string, SavedArticleFallback>;
  let fallbacksChanged = false;
  const keep = new Set<string>([
    ...Array.from(state.favorites || []),
    ...Array.from(state.readLater || []),
    ...Object.keys(state.offlineArticleAssets || {}),
  ]);

  Object.keys(incoming).forEach((id) => {
    if (!keep.has(id)) return;
    const incomingMeta = incoming[id];
    if (!incomingMeta || typeof incomingMeta !== "object") return;

    const localMeta = state.savedFallbacks[id];
    if (!localMeta) {
      state.savedFallbacks[id] = incomingMeta;
      fallbacksChanged = true;
      return;
    }

    const incomingSavedAt = Number(incomingMeta.savedAt || 0);
    const localSavedAt = Number(localMeta.savedAt || 0);
    if (incomingSavedAt >= localSavedAt) {
      const localJson = JSON.stringify(localMeta);
      const incomingJson = JSON.stringify(incomingMeta);
      if (localJson !== incomingJson) {
        state.savedFallbacks[id] = incomingMeta;
        fallbacksChanged = true;
      }
    }
  });

  if (fallbacksChanged) {
    try {
      saveSavedFallbacksCache();
    } catch (e) {
      console.warn("[Sync] Failed to persist merged fallbacks:", e);
    }
  }

  return fallbacksChanged;
}

export function applySyncState(
  syncState: unknown,
  serverTime?: number,
  revision?: number,
): boolean {
  if (!syncState || typeof syncState !== "object" || Array.isArray(syncState)) {
    return false;
  }

  if (typeof revision === "number" && revision > 0) {
    if (revision <= lastAppliedRevision) {
      return false;
    }
    lastAppliedRevision = revision;
  }

  if (serverTime) {
    if ((!revision || revision === 0) && serverTime < lastAppliedServerTime) {
      return false;
    }
    lastAppliedServerTime = Math.max(lastAppliedServerTime, serverTime);
  }

  let changed = false;
  const keys: SyncKey[] = ["readItems", "favorites", "readLater"];
  const syncStateRecord = syncState as Record<string, unknown>;

  keys.forEach((key) => {
    if (!(key in syncStateRecord) || syncStateRecord[key] === undefined) {
      return;
    }

    const incoming = syncStateRecord[key];
    const isValidStructure =
      Array.isArray(incoming) ||
      (incoming !== null &&
        typeof incoming === "object" &&
        Array.isArray((incoming as Record<string, unknown>).items));
    if (!isValidStructure) {
      return;
    }

    const { items: incomingItems, updatedAt: incomingUpdatedAt } = normalizeIncomingCollection(incoming);
    const current = setFromKey(key);
    const incomingSet: Set<string> = new Set<string>(incomingItems);
    const baseline = syncMetadata[key].baseline;

    const resolvedState = resolveCollectionState(current, baseline, incomingSet);
    if (resolvedState && !areSetsEqual(current, resolvedState)) {
      setStateFromKey(key, resolvedState);
      changed = true;
    }

    updateBaseline(key, incomingItems, incomingUpdatedAt);
  });

  if ("savedFallbacks" in syncStateRecord && syncStateRecord.savedFallbacks !== undefined) {
    const fallbacksChanged = mergeSavedFallbacksFromSync(syncState);
    if (fallbacksChanged) changed = true;
  }

  if (changed) {
    invalidateMemoCache();
  }

  pruneSavedFallbacks();
  saveSyncStateToCache();

  return changed;
}

export function initializeSyncBaseline(data: {
  readItems?: unknown;
  favorites?: unknown;
  readLater?: unknown;
  serverTime?: number;
  revision?: number;
}): void {
  applySyncState(
    {
      readItems: data.readItems,
      favorites: data.favorites,
      readLater: data.readLater,
    },
    data.serverTime,
    data.revision,
  );
  setSyncStatus("synced");
}

type CollectionSaveQueue = {
  inFlight: Promise<void> | null;
  queued: boolean;
  timer: ReturnType<typeof setTimeout> | null;
};

const collectionSaveLabels: Record<SyncKey, string> = {
  readItems: "ReadItems",
  favorites: "Favorites",
  readLater: "ReadLater",
};

const collectionSaveQueues: Record<SyncKey, CollectionSaveQueue> = {
  readItems: { inFlight: null, queued: false, timer: null },
  favorites: { inFlight: null, queued: false, timer: null },
  readLater: { inFlight: null, queued: false, timer: null },
};

function saveCollectionState(key: SyncKey, { immediate = false } = {}): Promise<void> {
  const queue = collectionSaveQueues[key];
  if (queue.timer) {
    clearTimeout(queue.timer);
  }

  saveSyncStateToCache();

  const delay = immediate ? 0 : 400;
  return new Promise<void>((resolve) => {
    queue.timer = setTimeout(() => {
      if (queue.inFlight) {
        queue.queued = true;
        if (key === "readItems") {
          console.log("[ReadItems] Save queued, another save in progress");
        }
        resolve();
        return;
      }

      const diff = diffSets(setFromKey(key), syncMetadata[key].baseline);
      if (diff.add.length === 0 && diff.remove.length === 0) {
        resolve();
        return;
      }

      const payload = {
        [key]: {
          add: diff.add,
          remove: diff.remove,
          updatedAt: Date.now(),
        },
      };

      setSyncStatus("syncing");
      queue.inFlight = API.post("/drive/sync-state", payload)
        .then((result) => {
          applySyncState(result.state, result.serverTime, result.revision);
          setSyncStatus("synced");
        })
        .catch((error) => {
          console.error(`[${collectionSaveLabels[key]}] SAVE FAILED:`, error);
          setSyncStatus(state.isOnline ? "error" : "offline");
        })
        .finally(() => {
          queue.inFlight = null;
          if (queue.queued) {
            queue.queued = false;
            void saveCollectionState(key, { immediate: true });
          }
          resolve();
        });
    }, delay);
  });
}

export function saveReadItems({ immediate = false } = {}): Promise<void> {
  return saveCollectionState("readItems", { immediate });
}

export function saveFavorites({ immediate = false } = {}): Promise<void> {
  return saveCollectionState("favorites", { immediate });
}

export function saveReadLater({ immediate = false } = {}): Promise<void> {
  return saveCollectionState("readLater", { immediate });
}

// Sync state from server
let lastSyncTime = 0;

export async function syncUserDataFromServer({ force = false } = {}): Promise<boolean> {
  const now = Date.now();
  if (!force && now - lastSyncTime < 30000) {
    return false;
  }
  lastSyncTime = now;

  try {
    setSyncStatus("syncing");
    const response = await API.get("/drive/load-sync-state");
    const changed = applySyncState(response.state, response.serverTime, response.revision);
    setSyncStatus("synced");
    return changed;
  } catch (error) {
    console.error("[Sync] Error syncing data:", (error as Error).message);
    setSyncStatus(state.isOnline ? "error" : "offline");
    return false;
  }
}

// Save feeds to localStorage cache
export function saveFeedsToCache() {
  try {
    const cacheData = {
      feedItems: feedItems,
      feedCounts: state.feedCounts,
      timestamp: Date.now(),
    };
    localStorage.setItem("rssio-feed-cache-v2", JSON.stringify(cacheData));
    localStorage.setItem("rssio-feed-cache-v2-time", String(Date.now()));
    console.log("[Cache] Saved feeds to cache");
  } catch (e) {
    console.warn("[Cache] Failed to save to cache:", e);
  }
}

// Load feeds from localStorage cache
export function loadFeedsFromCache() {
  try {
    const cacheTimeStr = localStorage.getItem("rssio-feed-cache-v2-time");
    if (!cacheTimeStr) return null;

    const cacheTime = parseInt(cacheTimeStr);
    const cacheAge = Date.now() - cacheTime;

    // On native (Capacitor), never expire the cache — always show something
    // On web, cache expires after 24 hours
    const isNative = Capacitor.isNativePlatform();
    if (!isNative && cacheAge > 24 * 60 * 60 * 1000) {
      localStorage.removeItem("rssio-feed-cache-v2");
      localStorage.removeItem("rssio-feed-cache-v2-time");
      return null;
    }

    const cached = JSON.parse(localStorage.getItem("rssio-feed-cache-v2") || "null");
    return { cached, cacheAge };
  } catch (e) {
    console.warn("[Cache] Failed to load cache:", e);
    return null;
  }
}

// Load feeds from IndexedDB cache (for offline fallback)
export async function loadFeedsFromIndexedDB(): Promise<FeedItems | null> {
  try {
    const allArticles = await getAllOfflineArticles();
    if (!allArticles || allArticles.length === 0) return null;

    // Group articles by feedId
    const feedItems: FeedItems = {};
    for (const article of allArticles) {
      const feedId = article.feedId || "unknown";
      if (!feedItems[feedId]) {
        feedItems[feedId] = [];
      }
      feedItems[feedId].push(article);
    }
    return feedItems;
  } catch (e) {
    console.warn("[Cache] Failed to load from IndexedDB:", e);
    return null;
  }
}

// Export the IndexedDB functions for use in other modules
export {
  getAllOfflineArticles,
  getAllOfflineArticleIds,
  saveArticleOffline,
  removeOfflineArticle,
  bulkDeleteOfflineArticles,
  bulkSaveArticlesOffline,
};
